import { User, UserManagerSettings } from "oidc-client";
import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react";
import { Route, Navigate, Routes } from "react-router-dom";

import { AuthService } from "./AuthService";

interface AuthContextValue {
  service: AuthService;
  user?: User|false;
  defaultAuthorizedRouteRedirect: string;
  defaultNotAuthorizedRouteRedirect: string;
}

const AuthContext = createContext<AuthContextValue|undefined>(undefined);

export interface AuthProviderProps {
  oidcSettings: UserManagerSettings;
  signInCallbackFallbackRoute?: string;
  defaultAuthorizedRouteRedirect?: string;
  defaultNotAuthorizedRouteRedirect?: string;
  signInCallbackRoute?: string;
  silentSignInCallbackRoute?: string;
  loadingComponentFactory?: () => JSX.Element;
}

export function AuthProvider(props: PropsWithChildren<AuthProviderProps>): JSX.Element {
  const {
    oidcSettings,
    signInCallbackFallbackRoute,
    defaultAuthorizedRouteRedirect,
    defaultNotAuthorizedRouteRedirect,
    signInCallbackRoute,
    silentSignInCallbackRoute,
    loadingComponentFactory,
    children,
  } = props;

  const [state, setState] = useState<AuthContextValue>({
    service: new AuthService(oidcSettings, signInCallbackFallbackRoute ?? "/"),
    user: undefined,
    defaultAuthorizedRouteRedirect: defaultAuthorizedRouteRedirect ?? "/",
    defaultNotAuthorizedRouteRedirect: defaultNotAuthorizedRouteRedirect ?? "/",
  })

  useEffect(() => {
    state.service.onUserUpdated = user => setState(current => ({ ...current, user }))
    state.service.initiate()
    return () => state.service.onUserUpdated = undefined
  }, [state.service]);

  return (
    <Routes>
      <Route path={signInCallbackRoute ?? "/callback"} element={<CompleteSignIn loading={loadingComponentFactory} service={state.service} />} />
      <Route path={silentSignInCallbackRoute ?? "/silent-callback"} element={<CompleteSilentSignIn service={state.service} />} />
      <Route path="*" element={<AuthContext.Provider value={state}>{children}</AuthContext.Provider>} />
    </Routes>
  );
}

export function useAuth(): AuthContextValue {
  const auth = useContext(AuthContext);
  if (auth === undefined) {
    throw new Error("useAuth called outside of AuthProvider");
  } else {
    return auth;
  }
}

interface CompleteSignInProps {
  service: AuthService;
  loading?: () => JSX.Element;
}

function CompleteSignIn({ service, loading }: CompleteSignInProps) {
  const [returnUrl, setReturnUrl] = useState<string|null>(null);

  useEffect(() => {
    const completeSignIn = async () => {
      const returnUrl = await service.completeSignIn();
      setReturnUrl(returnUrl);
    };
    completeSignIn();
  }, [service]);

  if (returnUrl) {
    return (
      <Navigate to={returnUrl} />
    );
  }  else {
    return loading?.call(undefined) ?? <></>;
  }
}

interface CompleteSilentSignInProps {
    service: AuthService;
}

function CompleteSilentSignIn({ service }: CompleteSilentSignInProps) {
  useEffect(() => {
    const completeRefresh = async () => {
      await service.completeRefresh();
    };
    completeRefresh();
  }, [service]);
  return <></>;
}